import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles';

// Icons
import FlightIcon from '@mui/icons-material/Flight';
import BusinessIcon from '@mui/icons-material/Business';
import CelebrationIcon from '@mui/icons-material/Celebration';
import RouteIcon from '@mui/icons-material/Route';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Custom components
import GridContainer from '../components/common/GridContainer';
import GridItem from '../components/common/GridItem';
import SEO from '../components/common/SEO';
import { pageSEO, generateStructuredData } from '../utils/seo';

const services = [
  {
    id: 'airport',
    title: 'Airport Transfers',
    description: 'Seamless transportation to and from airports, with flight monitoring, meet-and-greet service, and luggage assistance.',
    icon: <FlightIcon sx={{ fontSize: 40 }} />,
    features: [
      'Professional meet-and-greet service',
      'Assistance with luggage',
      'Curbside pickup or terminal meeting'
    ]
  },
  {
    id: 'corporate',
    title: 'Corporate Travel',
    description: 'Premium transportation solutions for executives and teams, including dedicated account management and corporate billing.',
    icon: <BusinessIcon sx={{ fontSize: 40 }} />,
    features: [
      'Dedicated corporate account management',
      'Expense tracking and reporting',
      'Centralized billing solutions',
      'Priority dispatch and VIP service'
    ]
  },
  {
    id: 'events',
    title: 'Event Transportation',
    description: 'Coordinated transportation for conferences, meetings, and corporate events, ensuring timely arrivals and departures.',
    icon: <CelebrationIcon sx={{ fontSize: 40 }} />,
    features: [
      'Event logistics coordination',
      'Multiple pickup and drop-off management',
      'Group transportation solutions',
      'On-site transportation director',
      'Custom branded signage available'
    ]
  },
  {
    id: 'roadshows',
    title: 'Road Shows',
    description: 'Meticulously planned transportation for multi-location business presentations, with detailed itineraries and contingency planning.',
    icon: <RouteIcon sx={{ fontSize: 40 }} />,
    features: [
      'Multi-city service coordination',
      'Detailed itinerary management',
      'Point-to-point scheduling',
      'Real-time updates and adjustments',
      'Designated road show coordinator'
    ]
  }
];

const ServicesPage: React.FC = () => {
  const theme = useTheme();
  const seoData = pageSEO['/services'];
  const structuredData = generateStructuredData('/services');

  return (
    <>
      <SEO 
        pathname="/services" 
        {...seoData} 
        structuredData={structuredData}
      />
      <Box
        sx={{
        minHeight: '100vh',
        background: `
          linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.05)} 100%
          ),
          radial-gradient(
            circle at 50% 50%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 0% 100%,
            ${alpha(theme.palette.secondary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 100% 0%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          )
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at 50% 50%,
              ${alpha(theme.palette.primary.main, 0.03)} 0%,
              transparent 70%
            )
          `,
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(1)',
              opacity: 1,
            },
            '50%': {
              transform: 'scale(1.2)',
              opacity: 0.8,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
            ),
            linear-gradient(
              -45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
          )`,
          backgroundSize: '60px 60px',
          opacity: 0.5,
          animation: 'move 20s linear infinite',
          '@keyframes move': {
            '0%': {
              backgroundPosition: '0 0',
            },
            '100%': {
              backgroundPosition: '60px 60px',
            },
          },
        }}
      />
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 10, md: 12 },
          mb: 6,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(to right, rgba(26, 43, 60, 0.9) 0%, rgba(26, 43, 60, 0.8) 50%, rgba(26, 43, 60, 0.7) 100%)`,
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: 'linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.1))',
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: theme.palette.secondary.main,
                fontWeight: 600,
                mb: 1,
                letterSpacing: 1,
                textTransform: 'uppercase',
              }}
            >
              OUR SERVICES
            </Typography>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontFamily: 'Playfair Display, serif',
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 3,
                lineHeight: 1.2,
              }}
            >
            Our Services
          </Typography>
            <Typography
              variant="h5"
              sx={{
                maxWidth: 800,
                opacity: 0.9,
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
            Comprehensive transportation solutions tailored to your corporate needs
          </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 6,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontFamily: 'Playfair Display, serif',
                fontWeight: 600,
                color: 'primary.main',
              }}
            >
            Premium Transportation Solutions
          </Typography>
          <Divider sx={{ mb: 3 }} />
            <Typography
              paragraph
              sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}
            >
            At The United Travel, we understand that reliable, professional transportation is essential for your business. 
            Our range of services is designed to meet the diverse needs of corporate clients, from airport transfers to 
            complex event logistics. Each service is delivered with our commitment to excellence, ensuring your 
            transportation needs are met with precision and style.
          </Typography>
            <Typography sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}>
            Explore our service offerings below and discover how we can elevate your corporate transportation experience.
          </Typography>
        </Paper>
        </motion.div>

        {services.map((service, index) => (
          <Box key={service.id} id={service.id} sx={{ scrollMarginTop: '100px' }}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Paper 
                elevation={0} 
                sx={{ 
                  p: { xs: 3, md: 4 },
                  mb: 6,
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.12)',
                    transform: 'translateY(-5px)',
                  },
                }}
              >
                <GridContainer spacing={4} alignItems="center">
                  <GridItem xs={12} md={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Box 
                        sx={{ 
                          color: 'primary.main',
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          bgcolor: 'rgba(26, 43, 60, 0.05)',
                        }}
                      >
                        {service.icon}
                      </Box>
                      <Typography
                        variant="h4"
                        component="h2"
                        sx={{
                          fontFamily: 'Playfair Display, serif',
                          fontWeight: 600,
                          color: 'primary.main',
                        }}
                      >
                        {service.title}
                      </Typography>
                    </Box>
                    
                    <Typography 
                      paragraph
                      sx={{ 
                        fontSize: { xs: '1rem', md: '1.1rem' },
                        mb: 3,
                        lineHeight: 1.8,
                      }}
                    >
                      {service.description}
                    </Typography>
                    
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        mb: 2,
                      }}
                    >
                      Key Features:
                    </Typography>
                    
                    <Box component="ul" sx={{ pl: 2, mb: 3, listStyle: 'none' }}>
                      {service.features.map((feature, idx) => (
                        <Typography
                          key={idx}
                          component="li"
                          paragraph
                          sx={{
                            fontSize: { xs: '0.95rem', md: '1rem' },
                            lineHeight: 1.8,
                            mb: 1.5,
                            '&::before': {
                              content: '"✓ "',
                              color: 'secondary.main',
                              fontWeight: 'bold',
                              mr: 1,
                            },
                          }}
                        >
                          {feature}
                        </Typography>
                      ))}
                    </Box>
                    
                    <Button
                      component={RouterLink}
                      to="/booking"
                      variant="contained"
                      color="primary"
                      endIcon={<ArrowForwardIcon />}
                      sx={{ mt: 2 }}
                      style={{ display: 'none' }}
                    >
                      Book This Service
                    </Button>
                  </GridItem>
                  
                  <GridItem xs={12} md={4}>
                    <Box
                      sx={{
                        bgcolor: 'grey.100',
                        height: 250,
                        width: '100%',
                        borderRadius: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Service Image Placeholder
                      </Typography>
                    </Box>
                  </GridItem>
                </GridContainer>
              </Paper>
            </motion.div>
          </Box>
        ))}
      </Container>
    </Box>
    </>
  );
};

export default ServicesPage; 